<template>
	<div>
		<div
			class="vcx-flex vcx-flex-wrap-smo vcx-gap-100 vcx-gap-200-md vcx-ml-300-md vcx-mb-200"
			data-test="chart-value-items"
		>
			<div class="vcx-flex vcx-justify-between">
				<NetReturnsOverTimeChartValueItem
					class="vcx-no-wrap"
					label="Value"
					label-classes="vcx-label-sm vcx-text-gray-30"
					:value="totalValue"
					:value-classes="['vcx-body-lg-bold']"
					:color="colors.accountValue.line"
				/>
			</div>
			<div class="vcx-flex vcx-gap-100 vcx-gap-200-md vcx-mb-100-smo">
				<NetReturnsOverTimeChartValueItem
					class="vcx-no-wrap"
					label="Contribution"
					label-classes="vcx-label-sm vcx-text-gray-30"
					:value="netContribution"
					:value-classes="['vcx-body-lg-bold']"
					:color="colors.netContributions.line"
				/>
				<NetReturnsOverTimeChartValueItem
					class="vcx-no-wrap"
					label="Net Return"
					label-classes="vcx-label-sm vcx-text-gray-30"
					:value="netReturn"
					:value-classes="['vcx-body-lg-bold']"
					:stretch="true"
				/>
			</div>
		</div>
		<div :style="`height: ${chartHeight}px`">
			<ValueOverTimeChart
				:custom-options="customOptions"
				:data="formattedChartData"
				:default-to-last-point="true"
				:reflow-chart="reflowChart"
				:sets="sets"
				@mouse-over="onMouseOver"
				@mouse-out="onMouseOut"
			></ValueOverTimeChart>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, onActivated, onDeactivated, ref } from 'vue';
import { ValueOverTimeChartPoint, ValueOverTimeChartSeries } from 'types/charts/value-over-time-chart';
import big from 'big.js';
import { HypotheticalInvestmentOverTimeChart } from 'types/funds';
import NetReturnsOverTimeChartValueItem from '@components/account/net-returns-over-time-chart-value-item.vue';
import { Options } from 'highcharts';
import spacetime from 'spacetime';
import { useAppStore } from '@stores/app';
import ValueOverTimeChart from '@charts/value-over-time-chart.vue';

interface Props {
	chartData: Array<HypotheticalInvestmentOverTimeChart>;
	initialInvestment?: string;
}

const props = defineProps<Props>();

const appStore = useAppStore();

const hoveredPoint = ref<ValueOverTimeChartPoint | null>(null);
const reflowChart = ref(false);

const chartHeight = computed((): number => (appStore.isMobile ? 250 : 350));

const colors = computed((): Record<'accountValue' | 'netContributions', { line: string; area: string }> => {
	return {
		accountValue: {
			area: '#335565',
			line: '#4C7E96'
		},
		netContributions: {
			area: '#fff',
			line: '#fff'
		}
	};
});

const customOptions = computed((): Options => {
	const tickInterval = big(props.initialInvestment ?? '0').gt(10000) ? 10000 : 1000;

	return {
		chart: { height: chartHeight.value, marginTop: 28, backgroundColor: 'transparent' },
		tooltip: {
			backgroundColor: 'transparent'
		},
		xAxis: {
			labels: {
				style: {
					color: '#9d9c98'
				}
			}
		},
		yAxis: {
			min: Number(props.initialInvestment ?? '0') - tickInterval,
			tickInterval,
			gridLineColor: '#6b6a65',
			gridLineDashStyle: 'Dash',
			labels: {
				style: {
					color: '#9d9c98'
				}
			}
		}
	};
});

const formattedChartData = computed((): Array<ValueOverTimeChartPoint> => props.chartData.map(formatPoint));

const lastPoint = computed((): ValueOverTimeChartPoint | null => {
	if (formattedChartData.value.length) {
		return formattedChartData.value[formattedChartData.value.length - 1];
	}

	return null;
});

const sets = computed((): Array<ValueOverTimeChartSeries> => {
	return [
		{
			color: colors.value.accountValue.line,
			name: 'Total Value',
			xKey: 'date',
			yKey: 'totalValue',
			enableMouseTracking: true,
			fillColor: {
				linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
				stops: [
					[0, '#335565'],
					[0.5, 'rgba(36, 52, 59, 0.8)'],
					[1, 'transparent']
				]
			}
		},
		{
			color: colors.value.netContributions.line,
			name: 'Net Contributions',
			xKey: 'date',
			yKey: 'netContributionsTd',
			enableMouseTracking: true,
			type: 'line',
			lineWidth: 1,
			dashStyle: '1, 1',
			fillColor: {
				stops: []
			}
		},
		{
			name: 'Total Value Line',
			xKey: 'date',
			yKey: 'totalValue',
			color: colors.value.accountValue.line,
			fillColor: { stops: [] }
		}
	];
});

const totalValue = computed((): string => {
	return hoveredPoint.value?.data?.totalValue.toString() ?? lastPoint.value?.data?.totalValue.toString() ?? '0';
});

const netContribution = computed((): string => {
	return (
		hoveredPoint.value?.data?.netContributionsTd.toString() ??
		lastPoint.value?.data?.netContributionsTd.toString() ??
		'0'
	);
});

const netReturn = computed((): string => {
	return hoveredPoint.value?.data?.netReturnsTd.toString() ?? lastPoint.value?.data?.netReturnsTd.toString() ?? '0';
});

onActivated((): void => {
	reflowChart.value = true;
});

onDeactivated((): void => {
	reflowChart.value = false;
});

function formatPoint(point: HypotheticalInvestmentOverTimeChart): ValueOverTimeChartPoint {
	return {
		data: {
			totalValue: +point.totalValue,
			netContributionsTd: +point.netContributionsTd,
			netReturnsTd: +point.netReturnsTd,
			date: spacetime(+point.chartDate).goto('utc').epoch
		}
	};
}

function onMouseOver(point: ValueOverTimeChartPoint): void {
	hoveredPoint.value = point;
}

function onMouseOut(): void {
	hoveredPoint.value = lastPoint.value;
}
</script>

<style scoped>
:deep(.highcharts-tooltip > span) {
	background: #cedde3;
	border-radius: 35px;
	padding: 6px 8px;
}
</style>
